import { chipBase } from 'components/ui/chip'
import styled from 'styled-components'
import { stoColor, stoMedia, stoSpace, stoType } from 'constants/stoTheme'

/**
 * Figma "Page" column (3329:24317): 12px down from the breadcrumb row, then 40px between the
 * identity block (ending with the metrics), the price card and the disclaimer; 32px on mobile
 * (3582:123343). The band around it (StoPage) owns the padding and the full-width rule below.
 */
export const HeroContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[10]};
  padding-top: ${stoSpace[3]};

  /* 24px from the breadcrumb row to the title, with the band's 8px gap. */
  ${stoMedia.belowDesktop} {
    gap: ${stoSpace[8]};
    padding-top: ${stoSpace[4]};
  }
`

/** Figma mobile 3582:128118: the ticker chip moves to the end of this row. */
export const BreadcrumbRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${stoSpace[2]};
`

export const Breadcrumb = styled.nav`
  ${stoType.bodySm};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${stoSpace[2]};
  color: ${stoColor.textSecondary};

  ${stoMedia.belowDesktop} {
    color: ${stoColor.textPrimary};

    /* Mobile drops the product name; the title right below already says it. */
    [aria-current='page'],
    :has(+ [aria-current='page']) {
      display: none;
    }
  }

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: ${stoColor.textPrimary};
      text-decoration: underline;
    }
  }
`

export const BreadcrumbSeparator = styled.span`
  color: ${stoColor.textSecondary};

  ${stoMedia.belowDesktop} {
    color: ${stoColor.textPrimary};
  }
`

/** Wraps the metrics row so mobile can reorder it; collapses with it when there are no metrics. */
export const MetricsSlot = styled.div`
  &:empty {
    display: none;
  }
`

export const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${stoSpace[2]};
`

/**
 * Figma "Heading 15": gap-space/6 down to the title block. Mobile (3582:126958) moves the chips
 * under the description (16px), then 32px to the metrics.
 */
export const IdentityBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[6]};

  ${stoMedia.belowDesktop} {
    gap: 0;

    > ${BadgeRow} {
      order: 1;
      margin-top: ${stoSpace[4]};
    }

    > ${MetricsSlot} {
      order: 2;
      margin-top: ${stoSpace[8]};
    }
  }
`

/**
 * Figma: neutral/200 pill; with a token icon it sits flush to the icon's side. Rendered
 * twice: in the badge row on desktop, at the end of the breadcrumb row (label first) on mobile.
 */
export const TickerBadge = styled.span<{ $placement: 'breadcrumb' | 'badges' }>`
  ${chipBase};
  gap: ${stoSpace[2]};
  padding: ${stoSpace[1]} ${stoSpace[2]};
  background: ${stoColor.neutral100};
  color: ${stoColor.textPrimary};
  /* Tickers keep their own casing (STRCst), unlike the other chips. */
  text-transform: none;

  /* Follows the rendered icon, so a chip whose icons all failed to load keeps its padding. */
  &:has(img) {
    padding: ${({ $placement }) => ($placement === 'breadcrumb' ? `0 0 0 ${stoSpace[3]}` : `0 ${stoSpace[3]} 0 0`)};
  }

  ${({ $placement }) => ($placement === 'breadcrumb' ? stoMedia.desktopUp : stoMedia.belowDesktop)} {
    display: none;
  }
`

/** Figma "Tranche Title": gap-space/2 between the title and the description. */
export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[2]};
`

/** The title, and on its right (desktop) the currency switch when there is one. */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${stoSpace[3]};
`

/**
 * The currency switch, kept its own size at the end of its row: by the title on desktop
 * (Figma 3362:95631), at the end of the chips on mobile (3582:125809). Both render; the other
 * placement is `display: none`, so out of the accessibility tree too.
 */
export const DenominationSlot = styled.div<{ $placement: 'title' | 'badges' }>`
  flex: 0 0 auto;
  margin-left: auto;
  ${({ $placement }) => ($placement === 'title' ? stoMedia.belowDesktop : stoMedia.desktopUp)} {
    display: none;
  }
`

export const Title = styled.h1`
  ${stoType.headingLg};
  flex: 1 1 auto;
  min-width: 0;
  /* Beside the switch a long single word breaks rather than running under it. */
  overflow-wrap: anywhere;
  margin: 0;
  color: ${stoColor.textPrimary};
`

export const Description = styled.p`
  ${stoType.heroDescription};
  margin: 0;
  color: ${stoColor.textPrimary};
`

/** Figma: below the identity block and the metrics row. */
export const Disclaimer = styled.p`
  ${stoType.disclaimer};
  margin: 0;
  color: ${stoColor.neutral500};
`
